"use client";

import { ArrowUpRight, Loader, MapPin, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inquiryTypes = [{
  id: 1,
  label: "Calculus"
}, {
  id: 2,
  label: "AP Physics"
}, {
  id: 3,
  label: "SAT Math"
}, {
  id: 4,
  label: "General"
}];
const contactBlocks = [{
  id: 1,
  label: "LOCATION",
  detail: "123 Academic Plaza, Innovation Hub, Tech City",
  icon: "pin"
}, {
  id: 2,
  label: "DIRECT",
  detail: "+1 (555) 234-8899",
  icon: "minus"
}, {
  id: 3,
  label: "INQUIRY",
  detail: "admissions@apextutor.com",
  icon: "arrow"
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card rounded-lg">
        <p className="font-default text-xl text-foreground mb-4">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white rounded-lg">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-primary font-mono">
              Student Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Alex Student" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary text-foreground placeholder:text-muted-foreground/50 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-primary font-mono">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="alex@example.com" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary text-foreground placeholder:text-muted-foreground/50 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-primary font-mono">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary text-foreground placeholder:text-muted-foreground/50 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiryType" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-primary font-mono">
              Subject Interest
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="border-0 border-b-2 border-border bg-transparent px-0 h-11 w-full focus-visible:ring-0 focus:border-secondary text-foreground font-default text-lg data-[placeholder]:text-muted-foreground/50">
                  <SelectValue placeholder="Select a subject" />
                </SelectTrigger>
                <SelectContent className="bg-white border-border rounded-lg">
                  <SelectItem value="calculus">Calculus</SelectItem>
                  <SelectItem value="physics">AP Physics</SelectItem>
                  <SelectItem value="sat">SAT Math</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-primary font-mono">
              Goal
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g., Improve SAT score by 200 points" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary text-foreground placeholder:text-muted-foreground/50 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-primary font-mono">
              Additional Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your academic goals..." {...field} className="border-0 border-b-2 border-border bg-transparent px-0 min-h-24 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary text-foreground placeholder:text-muted-foreground/50 font-default text-lg resize-none transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-lg">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-secondary text-white hover:bg-secondary/90 rounded-lg uppercase tracking-[0.25em] text-sm font-mono font-medium transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Submitting...
          </> : <>
            Request Consultation
            <ArrowUpRight className="ml-1 size-4" />
          </>}
      </Button>

      <p className="text-center text-sm italic text-muted-foreground font-default">
        We typically respond within 24 hours.
      </p>
    </Form>;
}
export default function ScheduleYourConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4408" id="schedule-your-consultation" className="bg-background text-foreground py-20 md:py-32 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-[0.4em] text-secondary font-mono mb-8">
              — Start Your Ascent
            </span>

            <h2 className="font-default font-semibold text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground mb-12">
              Architecting your
              <br />
              <span className="italic text-primary">academic success.</span>
            </h2>

            <div className="flex flex-col">
              {contactBlocks.map((block, i) => <div key={block.id} data-index={i} className="py-8 border-t border-border first:border-t-0">
                  <div className="flex items-start gap-6">
                    <div className="mt-1 shrink-0">
                      {block.icon === "pin" && <MapPin className="size-5 text-secondary" strokeWidth={1.5} />}
                      {block.icon === "minus" && <Minus className="size-5 text-secondary" strokeWidth={1.5} />}
                      {block.icon === "arrow" && <ArrowUpRight className="size-5 text-secondary" strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1">
                      <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-mono block mb-3">
                        {block.label}
                      </span>
                      <p className="font-default text-xl md:text-2xl text-foreground leading-snug">
                        {block.detail}
                      </p>
                    </div>
                  </div>
                </div>)}
              <div className="border-t border-border" />
            </div>

            <div className="mt-12">
              <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-mono block mb-5">
                Specialized Subjects
              </span>
              <div className="flex flex-wrap gap-3">
                {inquiryTypes.map((type, i) => <Badge key={type.id} data-index={i} variant="outline" className="border border-border text-foreground bg-accent px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-mono rounded-lg hover:bg-secondary/10 transition-colors">
                    {type.label}
                  </Badge>)}
              </div>
            </div>
          </div>

          <div className="lg:pl-8">
            <div className="bg-white border border-border rounded-lg p-8 md:p-12 shadow-md">
              <FormProvider formKey="schedule_consultation_resources" formId="schedule_consultation_resources" sectionName="schedule-your-consultation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiryType: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Request Consultation" successMessage="Thank you for your interest. We will review your goals and reach out shortly." errorMessage="Something went wrong. Please try again or contact us directly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}